import React, { useState, useEffect, useRef } from 'react';
import supabase from "../../../supabaseClient";
import { v4 as uuidv4 } from 'uuid';
import './CreateGroupModal.css';
import Select from 'react-select';
import { Switch } from '@mui/material';

const initialGroupState = (currentUser) => ({
  name: '',
  description: '',
  language: '',
  level: '',
  created_by: currentUser?.id,
  category: [],
  capacity: 10,
  schedules: [{ day: '', time: '', duration: 60 }],
  recurring: false,
  frequency: 'weekly',
  group_banner_url: null
});

const CreateGroupModal = ({ user, onClose, onGroupCreated }) => {
  const [newGroup, setNewGroup] = useState(initialGroupState(user));
  const [groupAvatar, setGroupAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Language options - comprehensive list matching AboutTab
  const LANGUAGE_OPTIONS = [
    "English", "Spanish", "French", "German", "Italian", "Portuguese", "Russian", "Chinese", "Japanese", "Korean", "Arabic", "Hindi", "Bengali", "Urdu", "Turkish", "Vietnamese", "Polish", "Dutch", "Greek", "Czech", "Swedish", "Finnish", "Norwegian", "Danish", "Hungarian", "Romanian", "Hebrew", "Thai", "Indonesian", "Malay", "Filipino", "Swahili", "Zulu", "Afrikaans", "Persian", "Ukrainian", "Serbian", "Croatian", "Slovak", "Bulgarian", "Slovenian", "Estonian", "Latvian", "Lithuanian", "Other"
  ];

  // Convert language array to react-select format
  const languageOptions = LANGUAGE_OPTIONS.map(lang => ({ value: lang, label: lang }));

  // CEFR level options matching AboutTab
  const levelOptions = [
    { value: "A1", label: "A1 (Beginner)" },
    { value: "A2", label: "A2 (Elementary)" },
    { value: "B1", label: "B1 (Intermediate)" },
    { value: "B2", label: "B2 (Upper Intermediate)" },
    { value: "C1", label: "C1 (Advanced)" },
    { value: "C2", label: "C2 (Proficient)" }
  ];

  // Category options matching AboutTab
  const categoryOptions = [
    { value: "Vocabulary", label: "Vocabulary" },
    { value: "Reading", label: "Reading" },
    { value: "Writing", label: "Writing" },
    { value: "Pronunciation", label: "Pronunciation" },
    { value: "Conversation (Speaking)", label: "Conversation (Speaking)" },
    { value: "Grammar", label: "Grammar" },
    { value: "Culture", label: "Culture" },
    { value: "Idioms", label: "Idioms" },
    { value: "Slang", label: "Slang" },
    { value: "Literature", label: "Literature" },
    { value: "Academic Writing", label: "Academic Writing" },
    { value: "Business Communication", label: "Business Communication" },
    { value: "Exam Preparation", label: "Exam Preparation" },
    { value: "Translation", label: "Translation" },
    { value: "Phonetics", label: "Phonetics" },
    { value: "Other", label: "Other" }
  ];

  const dayOptions = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' }
  ];

  // Image handlers for group avatar
  const handleGroupAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPG, PNG, GIF, and WEBP images are allowed.');
      return;
    }
    setGroupAvatar(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleRemoveGroupAvatar = () => {
    setGroupAvatar(null);
    setAvatarPreview(null);
    setNewGroup(prev => ({ ...prev, group_banner_url: null }));
  };

  // Schedule management
  const addSchedule = () => {
    setNewGroup((prev) => ({
      ...prev,
      schedules: [...(prev.schedules || []), { day: '', time: '', duration: 60 }]
    }));
  };

  const updateSchedule = (index, field, value) => {
    setNewGroup((prev) => ({
      ...prev,
      schedules: prev.schedules.map((schedule, i) =>
        i === index ? { ...schedule, [field]: value } : schedule
      )
    }));
  };

  const removeSchedule = (index) => {
    setNewGroup((prev) => ({
      ...prev,
      schedules: prev.schedules.filter((_, i) => i !== index)
    }));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGroup(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption, { name }) => {
    setNewGroup(prev => ({ ...prev, [name]: selectedOption.value }));
  };

  const handleMultiSelectChange = (selectedOptions, { name }) => {
    const values = selectedOptions.map(option => option.value);
    setNewGroup(prev => ({ ...prev, [name]: values }));
  };

  // Handle group form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let group_banner_url = newGroup.group_banner_url;

      if (groupAvatar) {
        const fileExt = groupAvatar.name.split('.').pop();
        const fileName = `banner_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `group-banners/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('banners')
          .upload(filePath, groupAvatar, { upsert: true });

        if (uploadError) {
          console.error('Error uploading group banner:', uploadError);
        } else {
          const { data } = supabase.storage
            .from('banners')
            .getPublicUrl(filePath);
          const publicUrl = data?.publicUrl;
          group_banner_url = publicUrl;
        }
      }

      const conversationId = uuidv4();

      const groupData = {
        ...newGroup,
        group_banner_url,
        created_at: new Date().toISOString()
      };

      // Remove schedules from groupData before inserting into groups table
      const { schedules, ...groupFields } = groupData;

      // Create a matching conversation for this group
      const { error: convError } = await supabase
        .from('conversations')
        .insert([
          {
            id: conversationId,
            is_group: true,
            group_name: groupFields.name,
            created_at: groupFields.created_at,
          },
        ]);

      if (convError) throw convError;

      // Create group using the same id as the conversation
      const { data: newGroupData, error: groupError } = await supabase
        .from('groups')
        .insert([{ ...groupFields, id: conversationId }])
        .select()
        .single();

      if (groupError) throw groupError;

      // After group creation, create a persistent virtual room for the group
      if (newGroupData?.id) {
        const { data: newRoom, error: roomError } = await supabase
          .from('rooms')
          .insert([
            {
              name: groupData.name,
              language: groupData.language,
              level: groupData.level,
              created_by: groupData.created_by,
              group_id: newGroupData.id // Link room to group
            }
          ])
          .select()
          .single();

        if (roomError) {
          console.error('Error creating virtual room:', roomError);
        } else {
          // Update group with room_id
          await supabase
            .from('groups')
            .update({ room_id: newRoom.id })
            .eq('id', newGroupData.id);
        }
      }

      // Save schedules to group_schedules table
      if (newGroupData?.id && schedules && schedules.length > 0) {
        // Insert new schedules
        const scheduleRows = schedules.map((sch) => ({
          group_id: newGroupData.id,
          day: sch.day,
          time: sch.time,
          duration: sch.duration
        }));
        await supabase.from('group_schedules').insert(scheduleRows);
      }

      // Add creator as a member of the group
      await supabase.from('group_members').insert([
        {
          group_id: newGroupData.id,
          user_id: user.id,
          role: 'Admin',
          joined_at: new Date().toISOString()
        }
      ]);

      // Notify parent component that group was created
      if (onGroupCreated) {
        onGroupCreated(newGroupData);
      }

      // Reset form
      setGroupAvatar(null);
      setAvatarPreview(null);
      setNewGroup(initialGroupState(user));
      onClose();
    } catch (error) {
      console.error('Error creating group:', error);
      alert('Error creating group. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-group-modal" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>×</button>
        <div className="modal-header">
          <h2>Create New Study Group</h2>
        </div>
        <form onSubmit={handleSubmit} className="create-group-form">
          <div className="form-group">
            <label>Group Name*</label>
            <input
              type="text"
              name="name"
              value={newGroup.name}
              onChange={handleInputChange}
              required
              placeholder="Enter group name"
            />
          </div>

          <div className="form-group">
            <label>Description*</label>
            <textarea
              name="description"
              value={newGroup.description}
              onChange={handleInputChange}
              required
              placeholder="Describe your group"
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Language*</label>
              <Select
                options={languageOptions}
                onChange={(option) => handleSelectChange(option, { name: 'language' })}
                placeholder="Select language"
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition={"fixed"}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  menu: base => ({ ...base, overflow: 'hidden' }),
                  menuList: base => ({ ...base, maxHeight: '160px' })
                }}
              />
            </div>

            <div className="form-group">
              <label>Level*</label>
              <Select
                options={levelOptions}
                onChange={(option) => handleSelectChange(option, { name: 'level' })}
                placeholder="Select level"
                className="react-select-container"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                menuPosition={"fixed"}
                styles={{
                  menuPortal: base => ({ ...base, zIndex: 9999 }),
                  menu: base => ({ ...base, overflow: 'hidden' }),
                  menuList: base => ({ ...base, maxHeight: '160px' })
                }}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Categories</label>
            <Select
              options={categoryOptions}
              isMulti
              onChange={(options) => handleMultiSelectChange(options, { name: 'category' })}
              placeholder="Select categories"
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition={"fixed"}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({ ...base, overflow: 'hidden' }),
                menuList: base => ({ ...base, maxHeight: '160px' })
              }}
            />
          </div>

          <div className="form-group">
            <label>Capacity</label>
            <input
              type="number"
              name="capacity"
              value={newGroup.capacity}
              onChange={handleInputChange}
              min="2"
              max="100"
            />
          </div>

          <div className="form-group">
            <label>Group Banner</label>
            <div className="avatar-upload-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleGroupAvatarChange}
                style={{ display: avatarPreview ? 'none' : 'block' }}
              />
              {avatarPreview && (
                <div className="avatar-preview-container">
                  <img src={avatarPreview} alt="Preview" className="avatar-preview" />
                  <button type="button" onClick={handleRemoveGroupAvatar} className="remove-avatar-btn">
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Meeting Schedule</label>
            {newGroup.schedules.map((schedule, index) => (
              <div key={index} className="schedule-row">
                <Select
                  options={dayOptions}
                  value={schedule.day ? { value: schedule.day, label: schedule.day } : null}
                  onChange={(option) => updateSchedule(index, 'day', option.value)}
                  placeholder="Day"
                  className="react-select-container schedule-select"
                  classNamePrefix="react-select"
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    menu: base => ({ ...base, overflow: 'hidden' }),
                    menuList: base => ({ ...base, maxHeight: '160px' })
                  }}
                />
                <input
                  type="time"
                  value={schedule.time}
                  onChange={(e) => updateSchedule(index, 'time', e.target.value)}
                  className="schedule-time"
                />
                <input
                  type="number"
                  value={schedule.duration}
                  onChange={(e) => updateSchedule(index, 'duration', parseInt(e.target.value))}
                  min="15"
                  max="240"
                  step="15"
                  className="schedule-duration"
                />
                <span className="duration-label">min</span>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => removeSchedule(index)}
                    className="remove-schedule-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addSchedule} className="add-schedule-btn">
              Add Schedule
            </button>
          </div>

          <div className="form-group recurring-toggle">
            <label>Recurring meetings</label>
            <div className="toggle-container">
              <span className="toggle-label">{newGroup.recurring ? 'Yes' : 'No'}</span>
              <Switch
                checked={!!newGroup.recurring}
                onChange={(e) => setNewGroup(prev => ({ ...prev, recurring: e.target.checked }))}
                color="primary"
              />
            </div>
          </div>

          {newGroup.recurring && (
            <div className="form-group frequency-select">
              <label>Frequency</label>
              <select
                name="frequency"
                value={newGroup.frequency || 'weekly'}
                onChange={handleInputChange}
                className="frequency-dropdown"
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every 2 weeks</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="create-btn" disabled={uploading}>
              {uploading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupModal;